/**
 * MonthlyBudgetCategoryCard Component
 *
 * 특정 월의 예산 오버라이드를 설정하기 위한 카드 컴포넌트
 *
 * 책임:
 * - 템플릿/오버라이드 토글
 * - 템플릿 금액 표시 (읽기 전용)
 * - 오버라이드 금액 입력
 * - 템플릿 대비 비교 표시 (+100,000원 ↑33%)
 * - 소분류 접기/펼치기 (Phase 1: 독립적 처리)
 */

import type { Category } from '@/domains/category/types/Category'
import type { Budget } from '../types/Budget'

/**
 * MonthlyBudgetCategoryCard Props
 */
export interface MonthlyBudgetCategoryCardProps {
  /** 카테고리 정보 */
  category: Category
  /** 템플릿 예산 (읽기 전용 표시용) */
  templateBudget: Budget | null
  /** 월별 오버라이드 예산 */
  monthlyBudget: Budget | null
  /** 오버라이드 활성화 여부 */
  isOverride: boolean
  /** 현재 입력된 금액 */
  amount: number
  /** 오버라이드 토글 핸들러 */
  onToggleOverride: () => void
  /** 금액 변경 핸들러 */
  onAmountChange: (value: number) => void
  /** 소분류 카테고리 목록 */
  minorCategories: Category[]
  /** 소분류 예산 맵 (minorId -> amount) */
  minorAmounts: Record<string, number>
  /** 소분류 오버라이드 상태 맵 */
  minorOverrideStates: Record<string, boolean>
  /** 소분류 오버라이드 토글 */
  onMinorToggleOverride: (minorId: string) => void
  /** 소분류 금액 변경 */
  onMinorAmountChange: (minorId: string, value: number) => void
  /** 소분류 펼침 상태 */
  expanded: boolean
  /** 펼침 토글 핸들러 */
  onToggle: () => void
}

/**
 * 특정 월의 카테고리별 예산 오버라이드 설정 카드
 *
 * 템플릿 사용 ↔ 이번 달 예산 설정 토글 제공
 * 오버라이드 모드에서는 템플릿과 비교 표시
 */
export function MonthlyBudgetCategoryCard({
  category,
  templateBudget,
  monthlyBudget: _monthlyBudget,
  isOverride,
  amount,
  onToggleOverride,
  onAmountChange,
  minorCategories,
  minorAmounts,
  minorOverrideStates,
  onMinorToggleOverride,
  onMinorAmountChange,
  expanded,
  onToggle,
}: MonthlyBudgetCategoryCardProps) {
  /**
   * 숫자 포맷팅
   */
  const formatCurrency = (num: number): string => {
    return num.toLocaleString('ko-KR')
  }

  /**
   * 템플릿 대비 차이 계산
   */
  const getDifference = (): { diff: number; percentage: number } | null => {
    if (!templateBudget || !isOverride) return null

    const diff = amount - templateBudget.amount
    const percentage =
      templateBudget.amount > 0
        ? (diff / templateBudget.amount) * 100
        : 0

    return { diff, percentage }
  }

  const difference = getDifference()

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
      {/* 카테고리 헤더 */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-2xl">{category.icon}</span>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            {category.name}
          </h3>
          {templateBudget && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              템플릿 예산: {formatCurrency(templateBudget.amount)}원
            </p>
          )}
        </div>
      </div>

      {/* 토글 스위치 */}
      <div className="flex items-center gap-3 mb-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isOverride}
            onChange={onToggleOverride}
            className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {isOverride ? '이번 달 예산 설정' : '템플릿 사용'}
          </span>
        </label>
      </div>

      {/* 예산 금액 입력/표시 */}
      <div className="mb-3">
        {isOverride ? (
          <div>
            {/* 오버라이드 모드: 입력 필드 */}
            <input
              type="number"
              inputMode="numeric"
              value={amount || ''}
              onChange={e => onAmountChange(parseInt(e.target.value) || 0)}
              placeholder="예산 금액 (원)"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
            />
            {/* 템플릿 대비 차이 표시 */}
            {difference && difference.diff !== 0 && (
              <div className="mt-2 text-sm">
                <span
                  className={
                    difference.diff > 0
                      ? 'text-orange-600 dark:text-orange-400 font-medium'
                      : 'text-green-600 dark:text-green-400 font-medium'
                  }
                >
                  {difference.diff > 0 ? '+' : ''}
                  {formatCurrency(difference.diff)}원 (
                  {difference.diff > 0 ? '↑' : '↓'}
                  {Math.abs(difference.percentage).toFixed(1)}%)
                </span>
              </div>
            )}
          </div>
        ) : (
          <div>
            {/* 템플릿 모드: 읽기 전용 표시 */}
            <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg">
              {templateBudget
                ? `${formatCurrency(templateBudget.amount)}원`
                : '예산 미설정'}
            </div>
          </div>
        )}
      </div>

      {/* 소분류 접기/펼치기 버튼 */}
      {minorCategories.length > 0 && (
        <button
          onClick={onToggle}
          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mb-2 flex items-center gap-1"
        >
          <span>{expanded ? '▼' : '▶'}</span>
          <span>소분류 설정 ({minorCategories.length}개)</span>
        </button>
      )}

      {/* 소분류 목록 */}
      {expanded && minorCategories.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 space-y-3">
          {minorCategories.map(minor => {
            const minorIsOverride = minorOverrideStates[minor.id] || false
            const minorAmount = minorAmounts[minor.id] || 0

            return (
              <div key={minor.id} className="space-y-2">
                {/* 소분류 헤더 */}
                <div className="flex items-center gap-2">
                  <span className="text-lg">{minor.icon}</span>
                  <span className="text-sm flex-1 text-gray-700 dark:text-gray-200">
                    {minor.name}
                  </span>
                </div>

                {/* 소분류 토글 */}
                <label className="flex items-center gap-2 cursor-pointer ml-6">
                  <input
                    type="checkbox"
                    checked={minorIsOverride}
                    onChange={() => onMinorToggleOverride(minor.id)}
                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="text-xs text-gray-600 dark:text-gray-300">
                    {minorIsOverride ? '이번 달 설정' : '템플릿 사용'}
                  </span>
                </label>

                {/* 소분류 금액 입력/표시 */}
                <div className="ml-6">
                  {minorIsOverride ? (
                    <input
                      type="number"
                      inputMode="numeric"
                      value={minorAmount || ''}
                      onChange={e =>
                        onMinorAmountChange(
                          minor.id,
                          parseInt(e.target.value) || 0
                        )
                      }
                      placeholder="0"
                      className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  ) : (
                    <div className="px-2 py-1 text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded">
                      템플릿 사용
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
